import React, { useState, useEffect } from 'react';
import Button from '../../components/common/button/Button';
import Modal from '../../components/common/model/Modal';
import styles from './PurchaseStockList.module.scss';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import purchaseService from '../../services/purchaseService';
import productService from '../../services/productService';
import stockService from '../../services/stockService';
import InputField from '../../components/common/inputField/InputField';
import { PurchaseStock } from '../../types/purchaseTypes';
import { Product } from '../../types/productTypes';

interface Stock {
  id: string;
  product_id: string;
  product_name?: string;
  full_stock_count: number;
  empty_stock_count: number;
}

type PurchaseType = 'new' | 'existing';

const PurchaseStockList: React.FC<{ role?: string }> = ({ role = 'admin' }) => {
  const [purchases, setPurchases] = useState<PurchaseStock[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  
  // Purchase type selection
  const [purchaseType, setPurchaseType] = useState<PurchaseType>('existing');
  
  // Form for existing product purchase
  const [form, setForm] = useState({
    product_id: '',
    purchase_stock: 0,
    total_purchase_amount: 0,
    empty_stock_count: 0
  });
  
  // Form for new product purchase
  const [newProductForm, setNewProductForm] = useState({
    product_name: '',
    product_price: 0,
    purchase_stock: 0,
    total_purchase_amount: 0,
    initial_empty_stock: 0
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingPurchaseId, setEditingPurchaseId] = useState<string | null>(null);
  const [originalEmptyCount, setOriginalEmptyCount] = useState<number>(0);

  const fetchPurchases = async () => {
    try {
      const response = await purchaseService.getPurchaseStock();

      const normalizedPurchases: PurchaseStock[] = Array.isArray(response)
        ? response
          .map((purchase): PurchaseStock | null => {
            if (!purchase || typeof purchase !== 'object') {
              console.warn('Invalid purchase object:', purchase);
              return null;
            }

            return {
              id: purchase.id ?? '',
              product_id: purchase.product_id ?? '',
              product_name: purchase.product_name ?? '',
              purchase_stock: Number(purchase.purchase_stock) || 0,
              total_purchase_amount: Number(purchase.total_purchase_amount) || 0,
              single_quantity_amount: Number(purchase.single_quantity_amount) || 0,
              empty_stock_count: Number(purchase.empty_stock_count) || 0,
              employee_id: purchase.employee_id ?? '',
              employee_name: purchase.employee_name,
              created_at: purchase.created_at ?? new Date().toISOString(),
              updated_at: purchase.updated_at ?? new Date().toISOString(),
            };
          })
          .filter((purchase): purchase is PurchaseStock => purchase !== null)
        : [];

      setPurchases(normalizedPurchases);
    } catch (err) {
      console.error('Error fetching purchases:', err);
      toast.error('Failed to fetch purchase stock');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productService.getProducts();
      const activeProducts = Array.isArray(response)
        ? response.filter(p => p.is_active !== false)
        : [];
      setProducts(activeProducts);
    } catch (err) {
      console.error('Error fetching products:', err);
      toast.error('Failed to fetch products');
    }
  };

  const fetchStocks = async () => {
    try {
      const response = await stockService.getStock();
      const normalizedStocks: Stock[] = Array.isArray(response)
        ? response
          .map((stock): Stock | null => {
            if (!stock || typeof stock !== 'object') {
              console.warn('Invalid stock object:', stock);
              return null;
            }

            return {
              id: stock.id ?? '',
              product_id: stock.product_id ?? '',
              product_name: stock.product_name ?? '',
              full_stock_count: stock.full_stock_count ?? 0,
              empty_stock_count: stock.empty_stock_count ?? 0
            };
          })
          .filter((stock): stock is Stock => stock !== null)
        : [];
      setStocks(normalizedStocks);
    } catch (err) {
      console.error('Error fetching stocks:', err);
    }
  };

  useEffect(() => {
    fetchPurchases();
    fetchProducts();
    fetchStocks();
  }, []);

  // Get available empty stock for a product
  const getAvailableEmptyStock = (productId: string): number => {
    const stock = stocks.find(s => Number(s.product_id) === Number(productId));
    let availableEmpty = stock?.empty_stock_count || 0;

    // In edit mode, add back the original empty count
    if (editMode && originalEmptyCount > 0) {
      availableEmpty += originalEmptyCount;
    }

    return availableEmpty;
  };

  // Handle adding purchase for existing product
  const handleAddExistingProduct = async () => {
    if (!form.product_id || form.purchase_stock <= 0 || form.total_purchase_amount <= 0) {
      toast.error('Please fill in all required fields with valid values.');
      return;
    }

    const availableEmpty = getAvailableEmptyStock(form.product_id);
    if (form.empty_stock_count > availableEmpty) {
      toast.error(`Only ${availableEmpty} empty cylinders available for exchange!`);
      return;
    }

    const data = {
      product_id: form.product_id,
      purchase_stock: form.purchase_stock,
      total_purchase_amount: form.total_purchase_amount,
      empty_stock_count: form.empty_stock_count
    };

    try {
      await purchaseService.createPurchaseStock(data);
      toast.success('Purchase stock created successfully!');
      setShowModal(false);
      resetForm();
      fetchPurchases();
      fetchStocks();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create purchase stock';
      toast.error(message);
    }
  };

  // Handle adding purchase for new product
  const handleAddNewProduct = async () => {
    if (!newProductForm.product_name || newProductForm.product_price <= 0) {
      toast.error('Please enter product name and price.');
      return;
    }

    if (newProductForm.purchase_stock <= 0 || newProductForm.total_purchase_amount <= 0) {
      toast.error('Please fill in purchase quantity and total amount.');
      return;
    }

    try {
      // Step 1: Create the new product with initial stock
      const productData = {
        name: newProductForm.product_name,
        price: newProductForm.product_price,
        full_stock_count: 0, // Will be updated by purchase
        empty_stock_count: newProductForm.initial_empty_stock
      };

      const newProduct = await productService.createProduct(productData);

      // Step 2: Create purchase stock for the new product
      const purchaseData = {
        product_id: newProduct.id,
        purchase_stock: newProductForm.purchase_stock,
        total_purchase_amount: newProductForm.total_purchase_amount,
        empty_stock_count: 0 // New product, no empty to exchange yet
      };

      await purchaseService.createPurchaseStock(purchaseData);

      toast.success('New product created and purchase stock added successfully!');
      setShowModal(false);
      resetForm();
      fetchProducts();
      fetchPurchases();
      fetchStocks();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create product and purchase stock';
      toast.error(message);
    }
  };

  const handleAdd = async () => {
    if (purchaseType === 'new') {
      await handleAddNewProduct();
    } else {
      await handleAddExistingProduct();
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this purchase stock? This will revert the stock changes.')) {
      return;
    }

    try {
      await purchaseService.deletePurchaseStock(id);
      toast.success('Purchase stock deleted successfully!');
      fetchPurchases();
      fetchStocks();
    } catch (err) {
      toast.error('Failed to delete purchase stock');
    }
  };

  const resetForm = () => {
    setForm({
      product_id: '',
      purchase_stock: 0,
      total_purchase_amount: 0,
      empty_stock_count: 0
    });
    setNewProductForm({
      product_name: '',
      product_price: 0,
      purchase_stock: 0,
      total_purchase_amount: 0,
      initial_empty_stock: 0
    });
    setOriginalEmptyCount(0);
    setPurchaseType('existing');
  };

  const handleOpenModal = () => {
    setShowModal(true);
    setPurchaseType('existing');
  };

  const handleOpenEditModal = (purchase: PurchaseStock) => {
    setForm({
      product_id: purchase.product_id || '',
      purchase_stock: purchase.purchase_stock || 0,
      total_purchase_amount: purchase.total_purchase_amount || 0,
      empty_stock_count: purchase.empty_stock_count || 0
    });
    setOriginalEmptyCount(purchase.empty_stock_count || 0);
    setEditingPurchaseId(purchase.id);
    setEditMode(true);
    setPurchaseType('existing'); // Edit is always for existing
    setShowModal(true);
  };

  const handleSaveEdit = async () => {
    if (!form.product_id || form.purchase_stock <= 0 || form.total_purchase_amount <= 0) {
      toast.error('Please fill in all required fields with valid values.');
      return;
    }

    const availableEmpty = getAvailableEmptyStock(form.product_id);
    if (form.empty_stock_count > availableEmpty) {
      toast.error(`Only ${availableEmpty} empty cylinders available for exchange!`);
      return;
    }

    if (!editingPurchaseId) return;

    const data = {
      id: editingPurchaseId,
      purchase_stock: form.purchase_stock,
      total_purchase_amount: form.total_purchase_amount,
      empty_stock_count: form.empty_stock_count
    };

    try {
      await purchaseService.updatePurchaseStock(data);
      toast.success('Purchase stock updated successfully!');
      setShowModal(false);
      setEditMode(false);
      setEditingPurchaseId(null);
      resetForm();
      fetchPurchases();
      fetchStocks();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update purchase stock';
      toast.error(message);
    }
  };

  const filteredPurchases = purchases.filter(purchase =>
    (purchase.product_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (purchase.employee_name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(price);
  };

  const calculateSingleQuantityAmount = () => {
    const stock = purchaseType === 'new' ? newProductForm.purchase_stock : form.purchase_stock;
    const amount = purchaseType === 'new' ? newProductForm.total_purchase_amount : form.total_purchase_amount;
    
    if (stock > 0 && amount > 0) {
      return (amount / stock).toFixed(2);
    }
    return '0.00';
  };

  // Get selected product's available empty stock for display
  const selectedProductEmptyStock = form.product_id
    ? getAvailableEmptyStock(form.product_id)
    : 0;

  return (
    <div className={styles.container}>
      <h4>Purchase Stock</h4>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.searchContainer}>
            <div className={styles.searchWrapper}>
              <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search by product or employee..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button
                  className={styles.clearButton}
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          {role === 'admin' && (
            <Button variant="primary" onClick={handleOpenModal}>
              + Add Purchase
            </Button>
          )}
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Product Name</th>
              <th>Purchase Quantity</th>
              <th>Empty Exchanged</th>
              <th>Total Amount</th>
              <th>Per Unit Cost</th>
              <th>Purchased By</th>
              <th>Purchase Date</th>
              {role === 'admin' && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={role === 'admin' ? 8 : 7} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading purchase stock...
                </td>
              </tr>
            ) : (
              Array.isArray(filteredPurchases) && filteredPurchases.length > 0 ? (
                filteredPurchases.map(purchase => {
                  if (!purchase || !purchase.id) {
                    console.warn('Invalid purchase in render:', purchase);
                    return null;
                  }

                  return (
                    <tr key={purchase.id}>
                      <td>{purchase.product_name || '-'}</td>
                      <td>{purchase.purchase_stock ?? 0}</td>
                      <td>{purchase.empty_stock_count ?? 0}</td>
                      <td>{formatPrice(purchase.total_purchase_amount)}</td>
                      <td>{formatPrice(purchase.single_quantity_amount)}</td>
                      <td>{purchase.employee_name || '-'}</td>
                      <td>{new Date(purchase.created_at).toLocaleString('en-IN', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</td>
                      {role === 'admin' && (
                        <td>
                          <div className={styles.actionIcons}>
                            <Button
                              onClick={() => handleOpenEditModal(purchase)}
                              className={styles.actionButton}
                              aria-label="Edit Purchase"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19.5 3 21l1.5-4L16.5 3.5z" />
                              </svg>
                            </Button>
                            <Button
                              onClick={() => handleDelete(purchase.id)}
                              className={styles.actionButton}
                              aria-label="Delete Purchase"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#ef4444"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                }).filter(row => row !== null)
              ) : (
                <tr>
                  <td colSpan={role === 'admin' ? 8 : 7} style={{ textAlign: 'center', padding: '20px' }}>
                    {searchTerm ? 'No purchase records found matching your search' : 'No purchase records found'}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditMode(false);
          setEditingPurchaseId(null);
          resetForm();
        }}
        title={editMode ? "Edit Purchase Stock" : "Add Purchase Stock"}
        size='lg'
      >
        <div className={styles.modalForm}>
          {/* Purchase Type Selection - Only show when not in edit mode */}
          {!editMode && (
            <div className={styles.purchaseTypeSelection}>
              <label className={styles.sectionLabel}>Purchase Type</label>
              <div className={styles.radioGroup}>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="purchaseType"
                    value="existing"
                    checked={purchaseType === 'existing'}
                    onChange={() => setPurchaseType('existing')}
                  />
                  <span className={styles.radioText}>Existing Product</span>
                  <span className={styles.radioDescription}>Add stock to an existing product</span>
                </label>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="purchaseType"
                    value="new"
                    checked={purchaseType === 'new'}
                    onChange={() => setPurchaseType('new')}
                  />
                  <span className={styles.radioText}>New Product</span>
                  <span className={styles.radioDescription}>Create a new product and add stock</span>
                </label>
              </div>
            </div>
          )}

          {/* Existing Product Form */}
          {(purchaseType === 'existing' || editMode) && (
            <>
              <div className={styles.formGroup}>
                <label htmlFor="product_id">Product *</label>
                <select
                  id="product_id"
                  name="product_id"
                  value={form.product_id}
                  onChange={e => {
                    setForm({
                      ...form,
                      product_id: e.target.value,
                      empty_stock_count: 0
                    });
                  }}
                  className={styles.selectField}
                  disabled={editMode}
                  required
                >
                  <option value="">Select a product</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>

              <InputField
                label="Purchase Quantity (Full Cylinders Received)"
                name="purchase_stock"
                type="number"
                value={form.purchase_stock}
                onChange={e => setForm({ ...form, purchase_stock: Number(e.target.value) })}
                placeholder="Enter purchase quantity"
                required
              />

              <div className={styles.formGroup}>
                <InputField
                  label={`Empty Cylinders to Exchange (Available: ${selectedProductEmptyStock})`}
                  name="empty_stock_count"
                  type="number"
                  value={form.empty_stock_count}
                  onChange={e => {
                    const value = Number(e.target.value);
                    if (value > selectedProductEmptyStock) {
                      toast.warning(`Only ${selectedProductEmptyStock} empty cylinders available!`);
                      return;
                    }
                    setForm({ ...form, empty_stock_count: value });
                  }}
                  placeholder="Enter empty cylinders to exchange"
                />
                {form.product_id && (
                  <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                    Empty cylinders will be reduced from stock when purchasing full cylinders
                  </small>
                )}
              </div>

              <InputField
                label="Total Purchase Amount"
                name="total_purchase_amount"
                type="number"
                value={form.total_purchase_amount}
                onChange={e => setForm({ ...form, total_purchase_amount: Number(e.target.value) })}
                placeholder="Enter total amount"
                required
              />
            </>
          )}

          {/* New Product Form */}
          {purchaseType === 'new' && !editMode && (
            <>
              <div className={styles.sectionDivider}>
                <span>Product Details</span>
              </div>

              <InputField
                label="Product Name *"
                name="product_name"
                type="text"
                value={newProductForm.product_name}
                onChange={e => setNewProductForm({ ...newProductForm, product_name: e.target.value })}
                placeholder="Enter product name"
                required
              />

              <InputField
                label="Selling Price (per unit) *"
                name="product_price"
                type="number"
                value={newProductForm.product_price}
                onChange={e => setNewProductForm({ ...newProductForm, product_price: Number(e.target.value) })}
                placeholder="Enter selling price"
                required
              />

              <InputField
                label="Initial Empty Stock (if any)"
                name="initial_empty_stock"
                type="number"
                value={newProductForm.initial_empty_stock}
                onChange={e => setNewProductForm({ ...newProductForm, initial_empty_stock: Number(e.target.value) })}
                placeholder="Enter initial empty cylinders (optional)"
              />

              <div className={styles.sectionDivider}>
                <span>Purchase Details</span>
              </div>

              <InputField
                label="Purchase Quantity (Full Cylinders Received) *"
                name="new_purchase_stock"
                type="number"
                value={newProductForm.purchase_stock}
                onChange={e => setNewProductForm({ ...newProductForm, purchase_stock: Number(e.target.value) })}
                placeholder="Enter purchase quantity"
                required
              />

              <InputField
                label="Total Purchase Amount *"
                name="new_total_purchase_amount"
                type="number"
                value={newProductForm.total_purchase_amount}
                onChange={e => setNewProductForm({ ...newProductForm, total_purchase_amount: Number(e.target.value) })}
                placeholder="Enter total amount"
                required
              />
            </>
          )}

          <div className={styles.calculatedField}>
            <label>Per Unit Cost (Calculated)</label>
            <div className={styles.calculatedValue}>
              {formatPrice(Number(calculateSingleQuantityAmount()))}
            </div>
          </div>

          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setEditMode(false);
                setEditingPurchaseId(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={editMode ? handleSaveEdit : handleAdd}
              className={styles.submitButton}
            >
              {editMode ? 'Save Changes' : (purchaseType === 'new' ? 'Create Product & Purchase' : 'Add Purchase')}
            </Button>
          </div>
        </div>
      </Modal>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default PurchaseStockList;